<template>
  <modal-vue
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    @close="handleClose"
    max-width="max-w-[800px]"
  >
    <template #header>
      <h2 class="text-xl font-bold text-textPrimary text-left">
        {{ headerTitle }}
      </h2>
      <p v-if="currentStep === 'form'" class="text-sm text-gray-600 mt-1 text-left">
        Selecione as empresas, unidades ou escalas que receberão as configurações importadas
      </p>
    </template>

    <StepForm
      v-if="currentStep === 'form'"
      v-model="form"
      v-model:valid="isFormValid"
      :policy-type="policyType"
    />

    <StepFile
      v-else-if="currentStep === 'file'"
      :model-value="selectedFile"
      :uploading="isImporting"
      :upload-progress="uploadProgress"
      :upload-loaded="uploadLoaded"
      :upload-total="uploadTotal"
      :policy-type="policyType"
      @update:model-value="onFileSelected"
    />

    <StepResult
      v-else
      :imported="importResult?.imported ?? 0"
      :rejected="importResult?.rejected ?? 0"
      :merged-duplicates="importResult?.mergedDuplicates ?? 0"
      :errors="importResult?.errors ?? []"
      :errors-truncated="importResult?.errorsTruncated ?? false"
      :policy-id="importResult?.policyId ?? 0"
      :policy-name="policyLabel"
    />

    <template #footer>
      <div class="flex items-center gap-4 w-full">
        <outlined-button
          v-if="showLeftButton"
          data-testid="import-voltar-button"
          :text="leftButtonText"
          width="100%"
          class="!border-primary30 !text-primary30 !font-bold flex-1"
          @click="handleBackAction"
        />
        <div v-else class="flex-1" />

        <filled-button
          :data-testid="rightButtonTestId"
          :text="rightButtonText"
          width="100%"
          :loading="isImporting || isValidatingSchedules"
          :disabled="isRightButtonDisabled"
          class="!bg-primary !text-white !font-bold hover:!bg-primary40 flex-1"
          @click="handleMainAction"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'

import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { useImportQualificationValidationsStore } from '@/pinia-store/useImportQualificationValidationsStore'
import type { ImportStep, ImportFormData, QualificationPolicyType } from './types'
import { DEFAULT_POLICY_TYPE } from './types'
import StepForm from './StepForm.vue'
import StepFile from './StepFile.vue'
import StepResult from './StepResult.vue'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    policyType?: QualificationPolicyType
  }>(),
  { policyType: DEFAULT_POLICY_TYPE }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  imported: []
}>()

const store = useImportQualificationValidationsStore()
const {
  isImporting,
  isValidatingSchedules,
  uploadProgress,
  uploadLoaded,
  uploadTotal,
  importResult
} = storeToRefs(store)

const currentStep = ref<ImportStep>('form')
const selectedFile = ref<File | null>(null)
const form = ref<ImportFormData>({
  company: null,
  healthUnit: null,
  schedules: []
})
const isFormValid = ref(false)

const policyLabel = computed(() =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? 'Qualificação Documental'
    : 'Qualificação para Repasse'
)

const headerTitle = computed(() => {
  switch (currentStep.value) {
    case 'form':
      return `Importar validações - ${policyLabel.value}`
    case 'file':
      return 'Selecione o arquivo'
    case 'result':
      return 'Importar validações'
    default:
      return ''
  }
})

const showLeftButton = computed(() => {
  if (currentStep.value === 'form') return true
  if (currentStep.value === 'file' && !isImporting.value) return true
  return false
})

const leftButtonText = computed(() => (currentStep.value === 'form' ? 'Cancelar' : 'Voltar'))

const rightButtonText = computed(() => {
  if (currentStep.value === 'form') return 'Selecionar arquivo'
  if (currentStep.value === 'file') return 'Salvar'
  if (currentStep.value === 'result') return 'Concluir'
  return ''
})

const rightButtonTestId = computed(() => {
  if (currentStep.value === 'form') return 'import-selecionar-button'
  if (currentStep.value === 'file') return 'import-importar-button'
  if (currentStep.value === 'result') return 'import-concluir-button'
  return ''
})

const isRightButtonDisabled = computed(() => {
  if (currentStep.value === 'form') return !isFormValid.value
  if (currentStep.value === 'file') return !selectedFile.value || isImporting.value
  return false
})

const handleBackAction = () => {
  if (currentStep.value === 'form') {
    handleClose()
  } else if (currentStep.value === 'file') {
    selectedFile.value = null
    currentStep.value = 'form'
  }
}

const validateAndProceedToFile = async () => {
  if (!form.value.company || !form.value.schedules.length) return
  const valid = await store.validateSchedules(
    form.value.company,
    form.value.schedules,
    props.policyType
  )
  if (valid && props.modelValue) currentStep.value = 'file'
}

const submitImport = async () => {
  if (!selectedFile.value || !form.value.company || !form.value.schedules.length) return

  const success = await store.importValidations(
    {
      companyId: form.value.company,
      scheduleIds: form.value.schedules,
      file: selectedFile.value
    },
    props.policyType
  )

  if (success && props.modelValue) currentStep.value = 'result'
}

const handleMainAction = async () => {
  if (currentStep.value === 'form') {
    await validateAndProceedToFile()
  } else if (currentStep.value === 'file') {
    await submitImport()
  } else if (currentStep.value === 'result') {
    handleClose()
  }
}

const onFileSelected = (file: File | null) => {
  selectedFile.value = file
}

let closeTimer: ReturnType<typeof setTimeout> | null = null

const resetModal = () => {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  currentStep.value = 'form'
  selectedFile.value = null
  form.value = { company: null, healthUnit: null, schedules: [] }
  isFormValid.value = false
  store.$reset()
}

const MODAL_CLOSE_ANIMATION_MS = 300

const handleClose = () => {
  if (closeTimer) clearTimeout(closeTimer)
  const shouldEmitImported =
    currentStep.value === 'result' && (importResult.value?.imported ?? 0) > 0
  if (shouldEmitImported) emit('imported')
  emit('update:modelValue', false)
  closeTimer = setTimeout(() => {
    if (!props.modelValue) resetModal()
    closeTimer = null
  }, MODAL_CLOSE_ANIMATION_MS)
}

onBeforeUnmount(() => {
  resetModal()
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) resetModal()
  }
)
</script>
